import { useCallback, useEffect, useRef } from 'react';
import { Colors, Flex, IconChevronRight, Typography } from '@banhala/ui-kit';
import { bridge, isApp, localizeNumber, isNotNil } from 'shared/src/utils';
import { ReviewSizeAnalysisType } from 'shared/src/api/goodsService/getSizeReviewAnalysis/types';
import { useSizeReviewAnalysis } from '../queries/useSizeReviewAnalysis';
import { useMemberInfo } from '../queries/useMemberInfo';
import { useLogGoodsAnalyticsEvent } from '../../../../../hooks/useLogGoodsAnalyticsEvent';
import { AnalysisProgressBar } from './AnalysisProgressBar';
import { useAppDownloadModal } from 'shared/src/hooks';

interface SizeReviewAnalysisProps {
  onHasData?: (hasData: boolean) => void;
}

export const SizeReviewAnalysis = ({ onHasData }: SizeReviewAnalysisProps) => {
  const { data: analysis, refetch } = useSizeReviewAnalysis();
  const { data: memberInfo } = useMemberInfo();
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();
  const hasLoggedDisplay = useRef(false);

  const height = memberInfo?.height;
  const weight = memberInfo?.weight;
  const sizeInfo = analysis?.size_info;
  const hasData = isNotNil(sizeInfo) && sizeInfo.total_count > 0;
  const { show: openModal } = useAppDownloadModal();

  useEffect(() => {
    refetch();
  }, [height, weight, refetch]);

  useEffect(() => {
    onHasData?.(hasData);
  }, [hasData, onHasData]);

  useEffect(() => {
    if (!hasData) return;
    if (hasLoggedDisplay.current) return;

    hasLoggedDisplay.current = true;
    logGoodsAnalytics({
      name: 'DISPLAY_SIZE_RECOMMENDATION',
      channel: ['ablylog'],
      analytics: {
        TAB: '사이즈',
      },
    });
  }, [hasData, logGoodsAnalytics]);

  const handleMoveMyPersonalization = useCallback(() => {
    logGoodsAnalytics({
      name: 'CLICK_MY_PERSONALIZATION',
    });
    if (isApp()) {
      bridge.openDeeplink('ably://my_personalization');
    } else {
      openModal();
    }
  }, [logGoodsAnalytics, openModal]);

  if (!hasData) {
    return null;
  }

  const { analysis_type, size_analysis, total_count } = sizeInfo;

  const myInfo =
    height || weight
      ? `${height ? height : '??'}cm · ${weight ? weight : '??'}kg`
      : null;

  return (
    <Flex padding={{ horizontal: 16 }}>
      <Flex
        backgroundColor="bg_secondary"
        borderRadius={8}
        padding={{ horizontal: 16, vertical: 16 }}
        gap={16}
      >
        {analysis_type === ReviewSizeAnalysisType.MULTI_SIZE ? (
          <Typography type="subtitle1" color="content_primary">
            나와 비슷한 체형이 구매한 사이즈 :{' '}
            <Typography type="subtitle1" color="content_blue_primary">
              {size_analysis[0].name}
            </Typography>
          </Typography>
        ) : (
          <Typography type="subtitle1" color="content_primary">
            나와 비슷한 체형{' '}
            <Typography type="subtitle1" color="content_blue_primary">
              {localizeNumber(total_count)}명
            </Typography>
            이 구매했어요
          </Typography>
        )}

        {myInfo && (
          <Flex.Row
            gap={2}
            alignItems="center"
            onPress={handleMoveMyPersonalization}
          >
            <Typography type="body3" color="content_secondary">
              {myInfo}
            </Typography>
            <IconChevronRight size={16} fill={Colors.content_tertiary} />
          </Flex.Row>
        )}

        {analysis_type === ReviewSizeAnalysisType.MULTI_SIZE && (
          <AnalysisProgressBar size_analysis={size_analysis} />
        )}

        {(!height || !weight) && (
          <Typography type="body3" color="content_secondary">
            *키와 체중을 모두 입력하면 정확한 정보를 얻을 수 있어요
          </Typography>
        )}
      </Flex>
    </Flex>
  );
};
